import type { TFunction } from 'i18next';
import type {
  AssessedArea,
  DataSource,
  DataSourceError,
  DataSourceRequest,
} from '../../api/workEnvironmentTypes';
import type { FieldError } from './assessmentView';
import { assessedLevel, RISK_LEVELS } from './riskView';

export const DATA_SOURCE_TYPES: DataSourceRequest['type'][] = [
  'SAFETY_INSPECTION',
  'EMPLOYEE_CONVERSATIONS',
  'HMS_REPORT',
  'SICK_LEAVE',
  'OTHER',
];

export const DATA_LEVELS = [1, 2, 3, 4, 5];

/** Highest risk first, leaving out levels without areas */
export const areasByLevel = (areas: AssessedArea[]) =>
  RISK_LEVELS.map((level) => ({ level, areas: areas.filter((area) => assessedLevel(area) === level) })).filter(
    (group) => group.areas.length > 0,
  );

export const sourcesFor = (areaKey: string, sources: DataSource[]) =>
  sources.filter((source) => source.factorKey === areaKey);

/** The date field in the local day */
export const today = (now = new Date()) =>
  [now.getFullYear(), now.getMonth() + 1, now.getDate()].map((part) => String(part).padStart(2, '0')).join('-');

/** A new data source starts as the kind of area it is about */
export const newDataSource = (area: AssessedArea, now = new Date()): DataSourceRequest => ({
  factorKey: area.key,
  type: 'SAFETY_INSPECTION',
  title: '',
  date: today(now),
  summary: '',
  dimension: area.dimension,
  level: 3,
});

export const DATA_SOURCE_FIELDS: Record<DataSourceError, string> = {
  area: 'data-source-title',
  type: 'data-source-type',
  title: 'data-source-title',
  date: 'data-source-date',
  summary: 'data-source-summary',
  dimension: 'data-source-dimension-LOAD',
  level: 'data-source-level-1',
};

export const dataSourceFieldErrors = (errors: DataSourceError[], t: TFunction): FieldError[] =>
  errors.map((error) => ({
    fieldId: DATA_SOURCE_FIELDS[error],
    message: t(`workEnvironmentRisk.data.errors.${error}`),
  }));

/** «Belastning 4 – høy» or «Ressurs 2 – svak» */
export const dataLevelLabel = (source: Pick<DataSource, 'dimension' | 'level'>, t: TFunction) =>
  t('workEnvironmentRisk.data.levelTag', {
    dimension: t(`hms.dimensions.${source.dimension}`),
    level: source.level,
    name: t(`workEnvironmentRisk.data.levels.${source.dimension}.${source.level}`).toLowerCase(),
  });
